/**
 * CharacterSheetSkeleton Component
 *
 * Purpose: Loading skeleton that matches character sheet layout
 * WHO: Users waiting for character data to load
 * WHAT: Placeholder animation matching final layout
 * WHERE: Character sheet page (loading state)
 * WHY: Smooth UX - shows shape of content before loaded (Option A decision)
 *
 * LAYOUT:
 * - Header (name, level, class)
 * - Ability scores (6 boxes)
 * - Features & abilities section
 * - Inventory section
 * - Backstory section
 */

import React from "react";
import {
  Box,
  VStack,
  HStack,
  Skeleton,
  SkeletonText,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Center,
} from "@chakra-ui/react";

export const CharacterSheetSkeleton: React.FC = () => {
  return (
    <VStack spacing={6} p={6} align="start" w="full">
      {/* Character Header - Name, Class, Level */}
      <VStack align="start" spacing={3} w="full">
        <Skeleton height="48px" width="70%" />
        <Skeleton height="24px" width="50%" />
      </VStack>

      <Divider />

      {/* Ability Scores Section */}
      <Box w="full">
        <Skeleton height="24px" width="200px" mb={4} />
        <SimpleGrid columns={6} spacing={3}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardBody p={4}>
                <VStack spacing={2} align="center">
                  <Skeleton height="16px" width="40px" />
                  <Skeleton height="28px" width="40px" />
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

      <Divider />

      {/* Features & Abilities Section */}
      <Box w="full">
        <Skeleton height="24px" width="250px" mb={4} />
        <VStack spacing={3} w="full">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} w="full">
              <CardBody>
                <VStack align="start" spacing={2} w="full">
                  <Skeleton height="20px" width="150px" />
                  <SkeletonText noOfLines={2} width="100%" spacing={2} />
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      </Box>

      <Divider />

      {/* Inventory Section */}
      <Box w="full">
        <Skeleton height="24px" width="150px" mb={4} />
        <VStack spacing={2} w="full">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height="40px" width="100%" />
          ))}
        </VStack>
      </Box>

      <Divider />

      {/* Backstory Section */}
      <Box w="full">
        <Skeleton height="24px" width="120px" mb={4} />
        <SkeletonText noOfLines={4} width="100%" spacing={3} />
      </Box>
    </VStack>
  );
};

export default CharacterSheetSkeleton;
